import { EV } from '../core/eventbus.js';
import { getSeat } from '../data/items.js';
import { canStand } from './movement.js';

/**
 * 姿态系统：站 / 坐 / 躺，以及原地起跳。
 *  - 靠近能坐的器物按 E：先坐上去（蒲团盘坐、木床躺下），再按 E 才是打坐、歇息
 *  - 空地按 C：就地坐下；再按 C 或推方向键起身
 *  - 按 J 或空格：起跳，落地回到地面（纯视觉高度，不改变碰撞）
 */
const GRAVITY = 560;
const JUMP_V = 195;   // 约 34px 高，比人略矮一点，落地干脆

// 起身时尝试的落脚点（单位：格，按优先级排序）
const STAND_OFFSETS = [
  [0, 1], [0, 1.6], [-1, 0.4], [1, 0.4], [-1.4, 0], [1.4, 0], [0, -0.7],
  [-1, 1.2], [1, 1.2], [0, 2.2], [-2, 0], [2, 0], [0, -1.4],
];

const tsOf = (scene) => (scene.tilemap ? scene.tilemap.tileSize : 40);

export const poseSystem = {
  name: 'pose',
  _bound: false,
  _ctx: null,

  update(dt, ctx) {
    const { scene, input, events, engine } = ctx;
    this._ctx = ctx;
    if (!this._bound) { this._bind(engine); this._bound = true; }

    const p = scene.first('transform', 'player');
    if (!p || !p.pose) return;
    // 在车上：不蹦也不坐，牢牢贴在座子里
    if (p.veh) { p.pose.z = 0; p.pose.vz = 0; return; }
    const pose = p.pose;
    const ts = tsOf(scene);

    // 空中：重力与落地
    if (pose.z > 0 || pose.vz !== 0) {
      pose.vz -= GRAVITY * dt;
      pose.z += pose.vz * dt;
      if (pose.z <= 0) {
        pose.z = 0;
        pose.vz = 0;
        events.emit(EV.PLAYER_LAND, {});
      }
    }

    // 起跳：站着、脚踏实地、不在布置模式。J 与空格都能跳（车上空格让给手刹）
    const editing = engine && engine.edit && engine.edit.active;
    if (!editing && pose.state === 'stand' && pose.z === 0
      && (input.justPressed('j') || input.justPressed(' '))) {
      pose.vz = JUMP_V;
      pose.z = 0.5;
      events.emit(EV.PLAYER_JUMP, {});
    }

    // C：就地坐下 / 起身
    if (!editing && input.justPressed('c')) {
      if (pose.state === 'stand') this.sitGround(ctx, p);
      else this.standUp(ctx, p);
    }

    // 坐/躺时推方向键 -> 起身（该帧后续的 movement 会正常走路）
    if (pose.state !== 'stand') {
      const a = input.axis();
      if (a.x !== 0 || a.y !== 0) this.standUp(ctx, p);
    }
  },

  _bind(engine) {
    if (!engine || !engine.events) return;
    engine.events.on(EV.POSE_SIT, (d) => {
      const ctx = this._ctx;
      if (!ctx) return;
      const p = ctx.scene.first('transform', 'player');
      if (p) this.sitOn(ctx, p, d.prop, d.seat);
    });
    engine.events.on(EV.POSE_UP, () => {
      const ctx = this._ctx;
      if (!ctx) return;
      const p = ctx.scene.first('transform', 'player');
      if (p) this.standUp(ctx, p);
    });
  },

  /** 坐上 / 躺上某件器物 */
  sitOn(ctx, p, prop, seatDef) {
    if (!prop || !p) return;
    const seat = seatDef || getSeat(prop.type);
    if (!seat) return;
    const ts = tsOf(ctx.scene);
    const pose = p.pose;

    pose.state = seat.pose;      // sit / lie
    pose.on = prop.id;
    pose.seatIndex = seat.index || 0;   // 游戏桌这类一桌多席，记住占的是哪条凳
    pose.z = 0;
    pose.vz = 0;

    p.transform.x = (prop.gx + seat.ox) * ts;
    p.transform.y = (prop.gy + seat.oy) * ts;
    p.anim.moving = false;
    p.anim.phase = 0;
    // 围着一桌坐时，人对着桌心；别的家具照旧朝前
    if (seat.pose === 'lie') p.anim.facing = 'up';
    else p.anim.facing = seat.facing || 'down';

    // 画在器物之后，人才不会陷进家具里；
    // 坐在「里侧」（像游戏桌北边那条凳）反过来先画人，桌子挡住半截腿，才像真围坐
    const behind = seat.facing === 'down' && seat.oy < (prop.h || 1) / 2;
    p.drawable.sortY = behind ? prop.gy * ts - 1 : (prop.gy + prop.h) * ts + 1;

    ctx.events.emit(EV.TOAST, { text: seat.pose === 'lie' ? '躺下了，按 E 歇息' : '坐下了', kind: 'info' });
  },

  /** 就地坐下（地上、地毯边，没有具体器物） */
  sitGround(ctx, p) {
    const pose = p.pose;
    pose.state = 'sit';
    pose.on = null;
    pose.seatIndex = undefined;
    pose.z = 0;
    pose.vz = 0;
    p.anim.moving = false;
    p.anim.phase = 0;
    p.drawable.sortY = undefined;
    ctx.events.emit(EV.TOAST, { text: '就地坐下，按 C 或挪一步起身', kind: 'info' });
  },

  /** 起身：站到器物旁边能落脚的地方 */
  standUp(ctx, p) {
    const pose = p.pose;
    if (pose.state === 'stand') return;
    const ts = tsOf(ctx.scene);
    const t = p.transform;
    const baseX = t.x;
    const baseY = t.y;

    pose.state = 'stand';
    pose.on = null;
    pose.seatIndex = undefined;
    pose.z = 0;
    pose.vz = 0;
    p.drawable.sortY = undefined;

    for (const [ox, oy] of STAND_OFFSETS) {
      const x = baseX + ox * ts;
      const y = baseY + oy * ts;
      if (canStand(ctx.scene, x, y)) {
        t.x = x;
        t.y = y;
        return;
      }
    }
    // 实在没地方站（被围死了）就原地起身，交给碰撞去挤
  },
};
